import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';

import { CONFIG } from '@frontend/config';
import {
  RouteMapPoint,
  routeMapPoints,
  routeMapPointsAreResolved,
  routeMapSrc,
} from '../routeMap';

interface Props {
  /** El vertical decide cómo se dibuja el trayecto (o si no se dibuja). */
  vertical: string;
  /**
   * Origen y destino a dibujar, con el rótulo de dónde salieron (el pedido o
   * el proveedor). Sin coordenadas usables no hay card.
   */
  points?: RouteMapPoint[] | null;
}

/**
 * El recorrido de la reserva en un mapa, con origen y destino marcados. Vive
 * en los momentos de DETALLE (el modal de reserva y el detalle post-booking):
 * nunca en las cards de resultados, donde N mapas se comerían el presupuesto
 * de densidad y cargarían un iframe por oferta.
 *
 * La nota de abajo dice de dónde salen los puntos QUE SE DIBUJAN, leída de
 * ellos mismos: con los que resolvió el proveedor es el recorrido confirmado,
 * y con los del pedido avisa que la dirección de la oferta es la que vale —
 * nadie tiene que adivinar cuál de las dos cosas está viendo.
 *
 * Sin coordenadas usables, sin modo para el vertical o sin key configurada, la
 * card entera no se renderiza — nunca un mapa vacío ni un recuadro roto.
 */
export function RouteMapCard({ vertical, points }: Props) {
  const { t, i18n } = useTranslation();

  // El saneo vive acá, en el punto único por el que pasan los dos orígenes de
  // datos (el formulario de búsqueda y la reserva persistida).
  const usable = routeMapPoints(points);
  // De los MISMOS puntos que se dibujan, nunca de un flag aparte: la nota no
  // puede describir un origen distinto del que produjo el mapa.
  const resolved = routeMapPointsAreResolved(usable);
  // La MISMA decisión que usa el control que ofrece abrir el mapa en la vista
  // de resultados (`SearchRouteMap`): si fueran dos expresiones parecidas, ese
  // control podría abrir una card que no se dibuja.
  const src = routeMapSrc({
    vertical,
    points: usable,
    apiKey: CONFIG.MAPS_EMBED_KEY,
    language: i18n.language,
  });
  if (!src) return null;

  // La línea de abajo nombra los puntos QUE SE DIBUJAN: si alguno no tiene
  // nombre no se escribe media ruta (leería como si el trayecto tuviera un
  // punto solo), queda el mapa con la nota.
  const labels = usable.every((point) => point.label)
    ? (usable.map((point) => point.label) as string[])
    : [];

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {t('travelServices.routeMap.title')}
      </Typography>
      <Box
        sx={(theme) => ({
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          overflow: 'hidden',
          lineHeight: 0,
        })}
      >
        <Box
          component="iframe"
          src={src}
          title={t('travelServices.routeMap.alt')}
          loading="lazy"
          // La key está restringida por dominio: el proveedor necesita el
          // origen para aceptarla, pero no tiene por qué ver la ruta interna
          // en la que está parado el asesor.
          referrerPolicy="origin"
          sx={{ display: 'block', width: '100%', height: 210, border: 0 }}
        />
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
        {labels.length === 2 && (
          <>
            <Box component="strong" mr={0.5}>A</Box>
            {labels[0]}
            <Box component="span" mx={0.5}>→</Box>
            <Box component="strong" mr={0.5}>B</Box>
            {labels[1]}
            <Box component="span" mx={0.5}>·</Box>
          </>
        )}
        {labels.length === 1 && (
          <>
            {labels[0]}
            <Box component="span" mx={0.5}>·</Box>
          </>
        )}
        {t(
          resolved
            ? 'travelServices.routeMap.resolvedPointsHint'
            : 'travelServices.routeMap.requestedPointsHint',
        )}
      </Typography>
    </Box>
  );
}
